import type { DecorationSpecsModelSnapshot } from "../types";
import type { SpecsAutoFormFieldMetadataMap } from "../autoFormMetadata";
import { ObservableModelNode } from "../observable";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readObjectArray,
  readString,
  splitKnown,
} from "../utils";
import { DecorationAssetModel } from "./DecorationAssetModel";
import { DecorationCreditsModel } from "./DecorationCreditsModel";
import { DecorationDesignTemplateModel } from "./DecorationDesignTemplateModel";
import { DecorationDisplayModel } from "./DecorationDisplayModel";
import { DecorationGeneratorModel } from "./DecorationGeneratorModel";
import { DecorationTechpackModel } from "./DecorationTechpackModel";
import { LocalizedTextModel } from "./LocalizedTextModel";

export class SpecsDecorationModel extends ObservableModelNode {
  static autoFormFields: SpecsAutoFormFieldMetadataMap = {
    id: { label: "ID", fieldType: "text" },
    name: { label: "Name", fieldType: "text" },
    factory: { label: "Factory", fieldType: "text" },
    group: { label: "Group", fieldType: "text" },
    behavesLike: { label: "Behaves like", fieldType: "text" },
    assetProcessing: { label: "Asset processing", fieldType: "text" },
    fileTypes: { label: "File types", fieldType: "json", minRows: 4 },
    legacyAssetTypes: { label: "Legacy asset types", fieldType: "stringList" },
    adminOnly: { label: "Admin only", fieldType: "boolean" },
    distributorOnly: { label: "Distributor only", fieldType: "boolean" },
    defaultErrorMessageOverwrite: {
      label: "Default error message overwrite",
      fieldType: "text",
    },
    extra: { label: "Extra JSON", fieldType: "json", minRows: 8 },
  };

  id!: string;
  name!: string;
  factory!: string;
  group!: string;
  behavesLike!: string;
  assetProcessing!: string;
  fileTypes!: unknown[];
  legacyAssetTypes!: string[];
  adminOnly!: boolean;
  distributorOnly!: boolean;
  defaultErrorMessageOverwrite!: string;
  displayName!: LocalizedTextModel | null;
  display!: DecorationDisplayModel | null;
  asset!: DecorationAssetModel | null;
  credits!: DecorationCreditsModel | null;
  techpack!: DecorationTechpackModel | null;
  designTemplates!: DecorationDesignTemplateModel[];
  generator!: DecorationGeneratorModel | null;
  tagSizes!: Record<string, unknown>[];
  repeatingPattern!: Record<string, unknown>;
  factoryPricing!: Record<string, unknown>;
  sizeLabel!: Record<string, unknown>;
  extra!: Record<string, unknown>;

  constructor() {
    super();
    this.defineObservableValue("id", "");
    this.defineObservableValue("name", "");
    this.defineObservableValue("factory", "");
    this.defineObservableValue("group", "");
    this.defineObservableValue("behavesLike", "");
    this.defineObservableValue("assetProcessing", "");
    this.defineObservableValue("fileTypes", []);
    this.defineObservableValue("legacyAssetTypes", []);
    this.defineObservableValue("adminOnly", false);
    this.defineObservableValue("distributorOnly", false);
    this.defineObservableValue("defaultErrorMessageOverwrite", "");
    this.defineObservableValue("displayName", null);
    this.defineObservableValue("display", null);
    this.defineObservableValue("asset", null);
    this.defineObservableValue("credits", null);
    this.defineObservableValue("techpack", null);
    this.defineObservableValue("designTemplates", []);
    this.defineObservableValue("generator", null);
    this.defineObservableValue("tagSizes", []);
    this.defineObservableValue("repeatingPattern", {});
    this.defineObservableValue("factoryPricing", {});
    this.defineObservableValue("sizeLabel", {});
    this.defineObservableValue("extra", {});
  }

  restore(snapshot: unknown) {
    const source = expectObject(snapshot, "decoration");
    this.id =
      typeof source.id === "string" && source.id.trim().length > 0
        ? source.id
        : readString(source, "name");
    this.name = readString(source, "name");
    this.factory = readString(source, "factory");
    this.group = readString(source, "group");
    this.behavesLike = readString(source, "behavesLike");
    this.assetProcessing = readString(source, "assetProcessing");
    this.fileTypes = Array.isArray(source.fileTypes)
      ? cloneValue(source.fileTypes)
      : [];
    this.legacyAssetTypes = Array.isArray(source.legacyAssetTypes)
      ? source.legacyAssetTypes.filter(
          (value): value is string => typeof value === "string"
        )
      : [];
    this.adminOnly = readBoolean(source, "adminOnly", false);
    this.distributorOnly = readBoolean(source, "distributorOnly", false);
    this.defaultErrorMessageOverwrite = readString(
      source,
      "defaultErrorMessageOverwrite"
    );
    this.displayName =
      source.displayName &&
      typeof source.displayName === "object" &&
      !Array.isArray(source.displayName)
        ? new LocalizedTextModel("decoration displayName").restore(
            source.displayName
          )
        : null;
    this.display =
      source.display &&
      typeof source.display === "object" &&
      !Array.isArray(source.display)
        ? new DecorationDisplayModel().restore(source.display)
        : null;
    this.asset =
      source.asset &&
      typeof source.asset === "object" &&
      !Array.isArray(source.asset)
        ? new DecorationAssetModel().restore(source.asset)
        : null;
    this.credits =
      source.credits &&
      typeof source.credits === "object" &&
      !Array.isArray(source.credits)
        ? new DecorationCreditsModel().restore(source.credits)
        : null;
    this.techpack =
      source.techpack &&
      typeof source.techpack === "object" &&
      !Array.isArray(source.techpack)
        ? new DecorationTechpackModel().restore(source.techpack)
        : null;
    this.designTemplates = readObjectArray(source, "designTemplates").map(
      (template) => new DecorationDesignTemplateModel().restore(template)
    );
    this.generator =
      source.generator &&
      typeof source.generator === "object" &&
      !Array.isArray(source.generator)
        ? new DecorationGeneratorModel().restore(source.generator)
        : null;
    this.tagSizes = readObjectArray(source, "tagSizes").map((entry) =>
      cloneValue(entry)
    );
    this.repeatingPattern =
      source.repeatingPattern &&
      typeof source.repeatingPattern === "object" &&
      !Array.isArray(source.repeatingPattern)
        ? cloneValue(source.repeatingPattern as Record<string, unknown>)
        : {};
    this.factoryPricing =
      source.factoryPricing &&
      typeof source.factoryPricing === "object" &&
      !Array.isArray(source.factoryPricing)
        ? cloneValue(source.factoryPricing as Record<string, unknown>)
        : {};
    this.sizeLabel =
      source.sizeLabel &&
      typeof source.sizeLabel === "object" &&
      !Array.isArray(source.sizeLabel)
        ? cloneValue(source.sizeLabel as Record<string, unknown>)
        : {};
    this.extra = splitKnown(source, [
      "id",
      "name",
      "factory",
      "group",
      "behavesLike",
      "assetProcessing",
      "fileTypes",
      "legacyAssetTypes",
      "adminOnly",
      "distributorOnly",
      "defaultErrorMessageOverwrite",
      "displayName",
      "display",
      "asset",
      "credits",
      "techpack",
      "designTemplates",
      "generator",
      "tagSizes",
      "repeatingPattern",
      "factoryPricing",
      "sizeLabel",
    ]);
    return this;
  }

  save(): DecorationSpecsModelSnapshot {
    return {
      ...cloneValue(this.extra),
      id: this.id,
      ...(this.name ? { name: this.name } : {}),
      ...(this.factory ? { factory: this.factory } : {}),
      ...(this.group ? { group: this.group } : {}),
      ...(this.behavesLike ? { behavesLike: this.behavesLike } : {}),
      ...(this.assetProcessing
        ? { assetProcessing: this.assetProcessing }
        : {}),
      ...(this.fileTypes.length > 0
        ? { fileTypes: cloneValue(this.fileTypes) }
        : {}),
      ...(this.legacyAssetTypes.length > 0
        ? { legacyAssetTypes: [...this.legacyAssetTypes] }
        : {}),
      ...(this.adminOnly ? { adminOnly: true } : {}),
      ...(this.distributorOnly ? { distributorOnly: true } : {}),
      ...(this.defaultErrorMessageOverwrite
        ? { defaultErrorMessageOverwrite: this.defaultErrorMessageOverwrite }
        : {}),
      ...(this.displayName ? { displayName: this.displayName.save() } : {}),
      ...(this.display ? { display: this.display.save() } : {}),
      ...(this.asset ? { asset: this.asset.save() } : {}),
      ...(this.credits ? { credits: this.credits.save() } : {}),
      ...(this.techpack ? { techpack: this.techpack.save() } : {}),
      ...(this.designTemplates.length > 0
        ? {
            designTemplates: this.designTemplates.map((template) =>
              template.save()
            ),
          }
        : {}),
      ...(this.generator ? { generator: this.generator.save() } : {}),
      ...(this.tagSizes.length > 0
        ? { tagSizes: this.tagSizes.map((entry) => cloneValue(entry)) }
        : {}),
      ...(Object.keys(this.repeatingPattern).length > 0
        ? { repeatingPattern: cloneValue(this.repeatingPattern) }
        : {}),
      ...(Object.keys(this.factoryPricing).length > 0
        ? { factoryPricing: cloneValue(this.factoryPricing) }
        : {}),
      ...(Object.keys(this.sizeLabel).length > 0
        ? { sizeLabel: cloneValue(this.sizeLabel) }
        : {}),
    };
  }
}
